import { Setting } from "view/components/SettingsList/types";
import { BillingDetails } from "store/exhibitors/cartStepper/helper";
import {
  emailValidationPattern,
  phoneValidationPattern,
} from "tools/validations";

import filterNotEmptyArray from "tools/common/filterNotEmptyArray";
import {
  IExhibitorPair,
  IExhibitorUser,
  IResolvedLocalMap,
  IShow,
} from "@evennt-tech/api-v2-core";

import { generateTestId } from "tools/cypress/generateTestId";
import { ShowUtils } from "tools/show";
import { UseTranslateResult } from "@tolgee/react";

type TranslateFn = UseTranslateResult["t"];

/** Every billing detail but the VAT number is required to go on. */
export const areBillingDetailsComplete = ({
  vat: _optionalVat,
  ...requiredFields
}: BillingDetails): boolean => Object.values(requiredFields).every(Boolean);

export const resolveBillingDetails = (
  billingDetails: BillingDetails,
  exhibitorPair: IExhibitorPair,
  exhibitorUser: IExhibitorUser
): Partial<BillingDetails> =>
  ({
    tradingName:
      billingDetails.tradingName ||
      exhibitorPair?.tradingName ||
      exhibitorPair?.companyToShow?.company?.name ||
      "",
    name:
      billingDetails.name ||
      exhibitorPair?.companyToShow?.company?.companyInfo?.registeredName ||
      exhibitorPair?.companyToShow?.company?.name ||
      "",
    vat:
      billingDetails.vat ||
      exhibitorPair?.companyToShow?.company?.companyInfo?.vat ||
      "",
    address:
      billingDetails.address ||
      exhibitorPair?.companyToShow?.company?.companyInfo?.address ||
      "",
    postCode:
      billingDetails.postCode ||
      exhibitorPair?.companyToShow?.company?.companyInfo?.postcode ||
      "",
    contact:
      billingDetails.contact ||
      exhibitorPair?.contactNameOrderOverride ||
      exhibitorUser.name,
    contactPhoneNumber:
      billingDetails.contactPhoneNumber || exhibitorPair.contactPhoneNumber,
    contactEmail: billingDetails.contactEmail || exhibitorUser?.user?.email,
  } as Partial<BillingDetails>);

export const exhibitorDetailsSettings = (
  details: BillingDetails,
  show: IShow,
  localMap: IResolvedLocalMap,
  t: TranslateFn
): Setting<Record<string, any>>[] => {
  const generateButtonId = (fieldName: string) => {
    const { id } = generateTestId(
      "exhibitor-cart-billing-details-edit",
      fieldName,
      "btn"
    );
    return id;
  };

  return [
    {
      buttonId: generateButtonId("trading-name"),
      title: `${t("exhibitor.cart.details.trading_name")} *`,
      value: details.tradingName,
      type: "text",
      fields: [
        {
          type: "text",
          name: "tradingName",
          label: t("exhibitor.cart.details.trading_name"),
          validation: { required: t("common.validation.required") },
          autoFocus: true,
        },
      ],
    },
    {
      buttonId: generateButtonId("registered-name"),

      title: `${t("exhibitor.cart.details.registered_name")} *`,
      value: details.name,
      type: "text",
      fields: [
        {
          type: "text",
          name: "name",
          label: t("exhibitor.cart.details.registered_name"),
          validation: { required: t("common.validation.required") },
          autoFocus: true,
        },
      ],
    },
    {
      buttonId: generateButtonId("sales-tax-number"),
      title: t("exhibitor.cart.details.tax_number_title", {
        taxName: show.salesTaxName,
      }),
      value: details.vat,
      type: "text",
      fields: [
        {
          type: "text",
          name: "vat",
          label: show.salesTaxName,
          validation: { required: false },
          autoFocus: true,
        },
      ],
    },
    {
      buttonId: generateButtonId("address"),
      title: `${t("exhibitor.cart.details.address")} *`,
      value: details.address,
      type: "text",
      fields: [
        {
          type: "text",
          name: "address",
          label: t("exhibitor.cart.details.address"),
          validation: { required: t("common.validation.required") },
          autoFocus: true,
        },
      ],
    },
    {
      buttonId: generateButtonId("post-code"),
      title: `${localMap.postCode} *`,
      value: details.postCode,
      type: "text",
      fields: [
        {
          type: "text",
          name: "postCode",
          label: localMap.postCode,
          validation: {
            maxLength: 10,
            required: t("common.validation.required"),
          },
          autoFocus: true,
        },
      ],
    },
    {
      buttonId: generateButtonId("contact-details"),

      title: `${t("exhibitor.cart.details.contact_details")} *`,
      value:
        details.contact &&
        details.contactPhoneNumber &&
        details.contactEmail &&
        `${details.contact}, ${details.contactPhoneNumber}, ${details.contactEmail}`,
      type: "text",
      fields: [
        {
          type: "text",
          name: "contact",
          label: t("exhibitor.cart.details.contact_name"),
          validation: { required: t("common.validation.required") },
          autoFocus: true,
        },
        {
          type: "text",
          name: "contactEmail",
          label: t("exhibitor.cart.details.contact_email"),
          validation: {
            pattern: {
              value: emailValidationPattern,
              message: t("common.validation.email"),
            },
            required: t("common.validation.required"),
          },
        },
        {
          type: "text",
          name: "contactPhoneNumber",
          label: t("exhibitor.cart.details.contact_phone_number"),
          validation: {
            pattern: {
              value: phoneValidationPattern,
              message: t("common.validation.phone"),
            },
            required: t("common.validation.required"),
          },
        },
      ],
    },
  ];
};
export const deliveryDetailsSettings = (
  exhibitorPair: IExhibitorPair,
  show: IShow,
  localMap: IResolvedLocalMap,
  t: TranslateFn
): Setting<Partial<IExhibitorPair & IShow>>[] => {
  const settings: (Setting<Partial<IExhibitorPair & IShow>> | null)[] = [
    !exhibitorPair?.stand?.builder?.hideStandNumber
      ? {
          title: t("exhibitor.cart.details.stand_number_title", {
            stand: localMap.stand,
          }),
          value: exhibitorPair?.stand?.name || "",
          type: "read-only",
        }
      : null,
    {
      title: t("exhibitor.cart.details.venue"),
      value: show.venueName,
      type: "read-only",
    },
    {
      title: t("exhibitor.cart.details.address"),
      value: ShowUtils.getAddress(show),
      type: "read-only",
    },
  ];
  return settings.filter(filterNotEmptyArray);
};
